import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { Diagnostic } from '@codemirror/lint';
import { EditorState } from '@codemirror/state';
import { EditorView, ViewPlugin, ViewUpdate } from '@codemirror/view';
import {
  getSearchQuery,
  searchPanelOpen,
  setSearchQuery,
  SearchQuery as SearchQueryType,
} from '@codemirror/search';
import type { Extension } from '@codemirror/state';
import { TranslationService } from './translation.service';

export type CodeEditorReservedWordsSection = 'snowSQLReservedWords';


export function prepareSqlInnerForLimitWrap(query: string): string {
  if (!query?.trim()) {
    return '';
  }
  let s = query.trim().replace(/;+$/, '');
  const lines = s.split(/\r?\n/);

  while (lines.length > 0) {
    const last = lines[lines.length - 1];
    if (last.trim() === '' || /^\s*--/.test(last)) {
      lines.pop();
    } else {
      break;
    }
  }

  const cleaned = lines.map(line =>
    line.replace(/\s+--.*$/, '').trimEnd()
  );
  return cleaned.join('\n').trim();
}

@Injectable({
  providedIn: 'root',
})
export class CodeEditorService {
  // subject to add content to editor wherever its current focus is at
  public addContent$ = new Subject();
  public snowSQLReservedWords = [
    {
      label: 'ACCOUNT',
      type: 'keyword',
    },
    {
      label: 'ALL',
      type: 'keyword',
    },
    {
      label: 'ALTER',
      type: 'keyword',
    },
    {
      label: 'AND',
      type: 'keyword',
    },
    {
      label: 'ANY',
      type: 'keyword',
    },
    {
      label: 'AS',
      type: 'keyword',
    },
    {
      label: 'BETWEEN',
      type: 'keyword',
    },
    {
      label: 'BY',
      type: 'keyword',
    },
    {
      label: 'CASE',
      type: 'keyword',
    },
    {
      label: 'CAST',
      type: 'keyword',
    },
    {
      label: 'CHECK',
      type: 'keyword',
    },
    {
      label: 'COLUMN',
      type: 'keyword',
    },
    {
      label: 'CONNECT',
      type: 'keyword',
    },
    {
      label: 'CONNECTION',
      type: 'keyword',
    },
    {
      label: 'CONSTRAINT',
      type: 'keyword',
    },
    {
      label: 'CREATE',
      type: 'keyword',
    },
    {
      label: 'CROSS',
      type: 'keyword',
    },
    {
      label: 'CURRENT',
      type: 'keyword',
    },
    {
      label: 'CURRENT_DATE',
      type: 'keyword',
    },
    {
      label: 'CURRENT_TIME',
      type: 'keyword',
    },
    {
      label: 'CURRENT_TIMESTAMP',
      type: 'keyword',
    },
    {
      label: 'CURRENT_USER',
      type: 'keyword',
    },
    {
      label: 'DATABASE',
      type: 'keyword',
    },
    {
      label: 'DELETE',
      type: 'keyword',
    },
    {
      label: 'DISTINCT',
      type: 'keyword',
    },
    {
      label: 'DROP',
      type: 'keyword',
    },
    {
      label: 'ELSE',
      type: 'keyword',
    },
    {
      label: 'EXISTS',
      type: 'keyword',
    },
    {
      label: 'FALSE',
      type: 'keyword',
    },
    {
      label: 'FOLLOWING',
      type: 'keyword',
    },
    {
      label: 'FOR',
      type: 'keyword',
    },
    {
      label: 'FROM',
      type: 'keyword',
    },
    {
      label: 'FULL',
      type: 'keyword',
    },
    {
      label: 'GRANT',
      type: 'keyword',
    },
    {
      label: 'GROUP',
      type: 'keyword',
    },
    {
      label: 'GSCLUSTER',
      type: 'keyword',
    },
    {
      label: 'HAVING',
      type: 'keyword',
    },
    {
      label: 'ILIKE',
      type: 'keyword',
    },
    {
      label: 'IN',
      type: 'keyword',
    },
    {
      label: 'INCREMENT',
      type: 'keyword',
    },
    {
      label: 'INNER',
      type: 'keyword',
    },
    {
      label: 'INSERT',
      type: 'keyword',
    },
    {
      label: 'INTERSECT',
      type: 'keyword',
    },
    {
      label: 'INTO',
      type: 'keyword',
    },
    {
      label: 'IS',
      type: 'keyword',
    },
    {
      label: 'ISSUE',
      type: 'keyword',
    },
    {
      label: 'JOIN',
      type: 'keyword',
    },
    {
      label: 'LATERAL',
      type: 'keyword',
    },
    {
      label: 'LEFT',
      type: 'keyword',
    },
    {
      label: 'LIKE',
      type: 'keyword',
    },
    {
      label: 'LOCALTIME',
      type: 'keyword',
    },
    {
      label: 'LOCALTIMESTAMP',
      type: 'keyword',
    },
    {
      label: 'MINUS',
      type: 'keyword',
    },
    {
      label: 'NATURAL',
      type: 'keyword',
    },
    {
      label: 'NOT',
      type: 'keyword',
    },
    {
      label: 'NULL',
      type: 'keyword',
    },
    {
      label: 'OF',
      type: 'keyword',
    },
    {
      label: 'ON',
      type: 'keyword',
    },
    {
      label: 'OR',
      type: 'keyword',
    },
    {
      label: 'ORDER',
      type: 'keyword',
    },
    {
      label: 'ORGANIZATION',
      type: 'keyword',
    },
    {
      label: 'QUALIFY',
      type: 'keyword',
    },
    {
      label: 'REGEXP',
      type: 'keyword',
    },
    {
      label: 'REVOKE',
      type: 'keyword',
    },
    {
      label: 'RIGHT',
      type: 'keyword',
    },
    {
      label: 'RLIKE',
      type: 'keyword',
    },
    {
      label: 'ROW',
      type: 'keyword',
    },
    {
      label: 'ROWS',
      type: 'keyword',
    },
    {
      label: 'SAMPLE',
      type: 'keyword',
    },
    {
      label: 'SCHEMA',
      type: 'keyword',
    },
    {
      label: 'SELECT',
      type: 'keyword',
    },
    {
      label: 'SET',
      type: 'keyword',
    },
    {
      label: 'SOME',
      type: 'keyword',
    },
    {
      label: 'START',
      type: 'keyword',
    },
    {
      label: 'TABLE',
      type: 'keyword',
    },
    {
      label: 'TABLESAMPLE',
      type: 'keyword',
    },
    {
      label: 'THEN',
      type: 'keyword',
    },
    {
      label: 'TO',
      type: 'keyword',
    },
    {
      label: 'TRIGGER',
      type: 'keyword',
    },
    {
      label: 'TRUE',
      type: 'keyword',
    },
    {
      label: 'TRY_CAST',
      type: 'keyword',
    },
    {
      label: 'UNION',
      type: 'keyword',
    },
    {
      label: 'UNIQUE',
      type: 'keyword',
    },
    {
      label: 'UPDATE',
      type: 'keyword',
    },
    {
      label: 'USING',
      type: 'keyword',
    },
    {
      label: 'VALUES',
      type: 'keyword',
    },
    {
      label: 'VIEW',
      type: 'keyword',
    },
    {
      label: 'WHEN',
      type: 'keyword',
    },
    {
      label: 'WHENEVER',
      type: 'keyword',
    },
    {
      label: 'WHERE',
      type: 'keyword',
    },
    {
      label: 'WITH',
      type: 'keyword',
    },
  ];

  constructor(private translationService: TranslationService) {}

  getReservedWords(
    customOptions: { label: string; type: string }[] = [],
    section: CodeEditorReservedWordsSection
  ) {
    return this?.[section]?.length
      ? [...this[section], ...customOptions]
      : [...customOptions];
  }


  private parseSnowflakeErrorLocation(error: string): {
    line: number;
    position: number;
    errorText: string | null;
  } | null {
    if (!error?.trim()) return null;
    const linePosMatch = error.match(
      /(?:error\s+)?line\s+(\d+)\s+at\s+position\s+(\d+)/i
    );
    if (!linePosMatch) return null;
    const line = parseInt(linePosMatch[1], 10);
    const position = parseInt(linePosMatch[2], 10);
    const quotedMatch = error.match(
      /(?:invalid\s+identifier|unexpected)\s*(?:"((?:[^"]|"")*)"|'((?:[^']|'')*)')/i
    );
    const rawQuoted = quotedMatch ? (quotedMatch[1] ?? quotedMatch[2]) : null;
    const errorText = rawQuoted
      ? rawQuoted.replace(/""/g, '"').replace(/''/g, "'")
      : null;
    return { line, position, errorText };
  }


  private offsetInText(text: string, line: number, position: number): number {
    const lines = text.split('\n');
    const lineIndex = Math.max(0, line - 1);
    let offset = 0;
    for (let i = 0; i < lineIndex && i < lines.length; i++) {
      offset += lines[i].length + 1;
    }
    return offset + Math.max(0, position);
  }

  private findErrorTextInLine(
    lineText: string,
    errorText: string
  ): { from: number; to: number } | null {
    const idx = lineText.indexOf(errorText);
    if (idx !== -1) return { from: idx, to: idx + errorText.length };
    const lower = lineText.toLowerCase();
    const lowerText = errorText.toLowerCase();
    const idxLower = lower.indexOf(lowerText);
    if (idxLower !== -1) return { from: idxLower, to: idxLower + errorText.length };
    return null;
  }

  formatSnowflakeErrors(
    error: string,
    attemptedQuery: string, // SQL that was actually sent (e.g. highlighted or first statement)
    fullQuery?: string // full worksheet content for mapping to editor positions
  ): Diagnostic[] {
    if (!attemptedQuery) return [];

    if (!fullQuery) fullQuery = attemptedQuery;

    const diagnostics: Diagnostic[] = [];

    // Find where the attempted query appears in the full document (so we map error positions correctly)
    let startIndex = fullQuery.indexOf(attemptedQuery);

    const parsed = this.parseSnowflakeErrorLocation(error);
    if (parsed) {
      const { line, position, errorText } = parsed;
      const attemptedLines = attemptedQuery.split('\n');
      const lineIndex = Math.max(0, line - 1);
      const lineStartInAttempted = this.offsetInText(attemptedQuery, line, 0);

      let fromInAttempted: number;
      let toInAttempted: number;

      if (errorText) {
        const currentLine =
          lineIndex < attemptedLines.length ? attemptedLines[lineIndex] : '';
        const rangeInLine = this.findErrorTextInLine(currentLine, errorText);
        if (rangeInLine) {
          fromInAttempted = lineStartInAttempted + rangeInLine.from;
          toInAttempted = lineStartInAttempted + rangeInLine.to;
        } else {
          const globalIdx = attemptedQuery.indexOf(errorText);
          if (globalIdx !== -1) {
            fromInAttempted = globalIdx;
            toInAttempted = globalIdx + errorText.length;
          } else {
            const fallbackStart = this.offsetInText(attemptedQuery, line, position);
            fromInAttempted = fallbackStart;
            toInAttempted = fallbackStart + errorText.length;
          }
        }
      } else {
        const fallbackStart = this.offsetInText(attemptedQuery, line, position);
        fromInAttempted = fallbackStart;
        toInAttempted = fallbackStart + 1;
      }

      let from: number;
      let to: number;
      if (startIndex !== -1) {
        from = startIndex + fromInAttempted;
        to = Math.min(startIndex + toInAttempted, fullQuery.length);
      } else {
        // Attempted query not found (e.g. trim/whitespace); try to find the error line in full document
        const errorLineText =
          lineIndex < attemptedLines.length ? attemptedLines[lineIndex] : '';
        const lineStartInFull = errorLineText
          ? fullQuery.indexOf(errorLineText)
          : -1;
        if (lineStartInFull !== -1) {
          if (errorText) {
            const rangeInLine = this.findErrorTextInLine(errorLineText, errorText);
            if (rangeInLine) {
              from = lineStartInFull + rangeInLine.from;
              to = lineStartInFull + rangeInLine.to;
            } else {
              from = lineStartInFull + Math.max(0, position);
              to = from + errorText.length;
            }
          } else {
            from = lineStartInFull + Math.max(0, position);
            to = from + 1;
          }
          to = Math.min(to, fullQuery.length);
        } else {
          // Last resort: search for the error identifier anywhere in full query
          const idx =
            errorText && fullQuery.indexOf(errorText) !== -1
              ? fullQuery.indexOf(errorText)
              : -1;
          if (idx !== -1 && errorText) {
            from = idx;
            to = idx + errorText.length;
          } else {
            return diagnostics;
          }
        }
      }

      const message = error.trim();
      const docLen = fullQuery.length;
      from = Math.max(0, Math.min(from, docLen));
      to = Math.max(from, Math.min(to, docLen));
      diagnostics.push({
        from,
        to,
        severity: 'error',
        message,
        source: 'Snowflake',
      });
    } else {
      // fallback for errors without line/position
      const quotedMatch = error.match(
        /(?:"((?:[^"]|"")*)"|'((?:[^']|'')*)')/
      );
      const rawQuoted = quotedMatch?.[1] ?? quotedMatch?.[2];
      const errorContent = rawQuoted
        ? rawQuoted.replace(/""/g, '"').replace(/''/g, "'")
        : undefined;
      if (errorContent) {
        const contentLower = errorContent.toLowerCase();
        let length = errorContent.length;
        let idx: number = -1;
        const attemptedLower = attemptedQuery.toLowerCase();
        const fullLower = fullQuery.toLowerCase();
        const startInFull =
          fullQuery !== attemptedQuery ? fullQuery.indexOf(attemptedQuery) : 0;
        const searchInAttemptedFirst = startInFull >= 0;
        if (searchInAttemptedFirst) {
          idx = attemptedLower.indexOf(contentLower);
          if (idx === -1 && errorContent.includes('.')) {
            const segment = errorContent.split('.').pop() ?? errorContent;
            const segmentIdx = attemptedLower.indexOf(segment.toLowerCase());
            if (segmentIdx !== -1) {
              idx = segmentIdx;
              length = segment.length;
            }
          }
          if (idx !== -1) idx = startInFull + idx;
        }
        if (idx === -1) {
          idx = fullLower.indexOf(contentLower);
          if (idx === -1 && errorContent.includes('.')) {
            const segment = errorContent.split('.').pop() ?? errorContent;
            const segmentIdx = fullLower.indexOf(segment.toLowerCase());
            if (segmentIdx !== -1) {
              idx = segmentIdx;
              length = segment.length;
            }
          }
        }
        if (idx !== -1) {
          const docLen = fullQuery.length;
          let from = Math.max(0, Math.min(idx, docLen));
          let to = Math.max(from, Math.min(idx + length, docLen));
          diagnostics.push({
            from,
            to,
            severity: 'error',
            message: error,
            source: 'Snowflake',
          });
        }
      }
    }

    return diagnostics;
  }

  private static readonly SEARCH_MATCH_COUNT_CAP = 1000;


  createSearchMatchCountExtension(): Extension {
    const self = CodeEditorService;
    const countClass = 'cm-search-match-count';
    const label = this.translationService.getTranslatedContent(
      'search_count',
      'Search Count'
    );

    const plugin = ViewPlugin.fromClass(
      class {
        countEl: HTMLElement | null = null;

        constructor(readonly view: EditorView) {
          this.updateCount(view);
        }

        update(update: ViewUpdate): void {
          if (!searchPanelOpen(update.view.state)) {
            this.ensureCountRemoved();
            return;
          }
          this.updateCount(update.view);
        }

        private updateCount(view: EditorView): void {
          if (!searchPanelOpen(view.state)) {
            this.ensureCountRemoved();
            return;
          }
          const query = getSearchQuery(view.state);
          const count = query.valid ? self.countMatches(query, view.state) : 0;
          const countText =
            count >= self.SEARCH_MATCH_COUNT_CAP ? '999+' : String(count);
          const text = `${label} ${countText}`;
          const panelEl =
            view.dom.querySelector('.cm-search') ??
            view.dom.parentElement?.querySelector('.cm-search');
          if (!panelEl) return;
          let el = panelEl.querySelector(`.${countClass}`) as HTMLElement | null;
          if (!el) {
            el = document.createElement('span');
            el.className = countClass;
            el.setAttribute('aria-live', 'polite');
            el.style.marginLeft = 'auto';
            el.style.marginRight = '0.5em';
            panelEl.appendChild(el);
          }
          el.textContent = text;
          this.countEl = el;
        }

        private ensureCountRemoved(): void {
          this.countEl = null;
        }

        destroy(): void {
          this.countEl?.remove();
        }
      }
    );

    return plugin;
  }

  private static countMatches(query: SearchQueryType, state: EditorState): number {
    let n = 0;
    try {
      const it = query.getCursor(state);
      for (;;) {
        const r = it.next();
        if (r.done) break;
        n++;
        if (n >= this.SEARCH_MATCH_COUNT_CAP) break;
      }
    } catch {
      return 0;
    }
    return n;
  }
}
